import { Link, To } from 'react-router-dom'
import ProductCollectionTitle from '@/modules/collections/components/ProductCollectionTitle'
import { CollectionProduct } from '@/modules/collections/types/collection.types'

interface NavProductCardProps {
	product: CollectionProduct
	to: To
	onClick: () => void
	formatPrice: (price: number, withCurrencySymbol: boolean) => string
	variant: 'desktop' | 'mobile'
}

const getProductImage = (product: CollectionProduct): string => {
	const preferredImage = product.images.find((image) => image.primary1)?.primary1
	if (preferredImage) {
		return preferredImage
	}

	for (const image of product.images) {
		const firstImageValue = Object.values(image)[0]
		if (typeof firstImageValue === 'string' && firstImageValue.length > 0) {
			return firstImageValue
		}
	}

	return ''
}

const NavProductCard = ({ product, to, onClick, formatPrice, variant }: NavProductCardProps) => {
	const isDesktop = variant === 'desktop'
	const imageUrl = getProductImage(product)
	const hoverUrl = product.images.find(image => image['primary1-hover'])?.['primary1-hover']
	const imageFit = product.imageFit || 'contain'
	const hoverImageFit = product.hoverImageFit || imageFit

	return (
		<Link
			to={to}
			className={`block rounded-2xl bg-background transition-colors overflow-hidden shadow-[0_2px_7px_rgba(0,0,0,0.2)] ${isDesktop ? 'duration-300 group md:hover:shadow-[0_8px_16px_rgba(0,0,0,0.24)]' : ''
				}`}
			onClick={onClick}
		>
			<div className={`bg-white relative ${isDesktop ? 'aspect-[5/4]' : 'aspect-[8/7]'}`}>
				{imageUrl ? (
					<img src={imageUrl} alt={product.productName} className={`w-full h-full ${imageFit === 'contain' ? 'p-2' : ''}`} style={{ objectFit: imageFit }} />
				) : (
					<div className={`w-full h-full flex items-center justify-center text-gray-400 ${isDesktop ? 'text-sm' : 'text-xs'}`}>
						No image
					</div>
				)}
				{isDesktop && hoverUrl && <img src={hoverUrl} alt="" className={`absolute inset-0 w-full h-full opacity-0 group-hover:opacity-100 transition-opacity ${hoverImageFit === 'contain' ? 'p-2' : ''}`} style={{ objectFit: hoverImageFit }} />}
				{product.badges && product.badges.length > 0 && (
					<div className={`absolute z-10 flex flex-wrap ${isDesktop ? 'top-4 left-4 right-4 gap-2' : 'top-2 left-2 right-2 gap-1'}`}>
						{product.badges.map((badge, badgeIndex) => (
							<span
								key={badgeIndex}
								className={`rounded-full font-medium text-white ${isDesktop ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-[10px]'}`}
								style={{ backgroundColor: badge.colorHex, color: badge.textColorHex || '#FFFFFF' }}
							>
								{badge.label}
							</span>
						))}
					</div>
				)}
			</div>
			<div className={isDesktop ? 'px-4 py-3' : 'px-3 py-3'}>
				<ProductCollectionTitle
					as={isDesktop ? 'h4' : 'p'}
					className={isDesktop
						? 'font-headline text-lg text-orange mb-1 md:group-hover:scale-[103%] transition-transform duration-300'
						: `font-headline text-base text-orange mb-0.5 ${product.titleHex === 'SPECIAL_CUSTOM' ? '' : 'truncate'}`}
					title={product.title}
					titleHex={product.titleHex}
				/>
				{typeof product.price === 'number' && (
					<p className="text-sm text-gray-800 mt-1">{formatPrice(product.price, true)}</p>
				)}
			</div>
		</Link>
	)
}

export default NavProductCard
